import { useEffect, useState } from 'react';

/**
 * Custom hook that returns a debounced version of the input value, along with a boolean indicating if it's currently debouncing.
 * @param value The value to debounce.
 * @param delay The debounce delay in milliseconds.
 * @returns A tuple containing the debounced value and a boolean indicating if it's currently debouncing.
 */
const useDebounced = <T, >(value: T, delay = 300): readonly [T, boolean] => {
  const [debounced, setDebounced] = useState<T>(value);
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    let id: ReturnType<typeof setTimeout> | null = null;

    setIsDebouncing(true);
    id = setTimeout(() => {
      setDebounced(value);
      setIsDebouncing(false);
    }, delay);

    return () => {
      if (id) {
        clearTimeout(id);
      }
      setIsDebouncing(false);
    };
  }, [value, delay]);

  return [debounced, isDebouncing] as const;
};

export default useDebounced;
